import type { AuthContext } from '@auth/app/core/models/auth.model';
import { decodeJwtPayload } from './jwt-role.util';

export interface TokenProfile {
  displayName: string;
  systemName: string;
}

function readStringClaim(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed || null;
}

export function extractDisplayName(payload: Record<string, unknown>): string {
  const name = readStringClaim(payload, 'name');
  if (name) {
    return name;
  }

  const givenName = readStringClaim(payload, 'given_name');
  const familyName = readStringClaim(payload, 'family_name');
  const fullName = [givenName, familyName].filter(Boolean).join(' ').trim();
  if (fullName) {
    return fullName;
  }

  return readStringClaim(payload, 'preferred_username') ?? '';
}

function formatSystemName(applicationCode: string, tenantCode: string): string {
  return `${applicationCode} · ${tenantCode}`.toUpperCase();
}

export function extractSystemName(
  payload: Record<string, unknown>,
  context?: AuthContext | null,
): string {
  const applicationCode =
    readStringClaim(payload, 'application_code') ?? context?.applicationCode ?? '';
  const tenantCode = readStringClaim(payload, 'tenant_code') ?? context?.tenantCode ?? '';

  if (applicationCode && tenantCode) {
    return formatSystemName(applicationCode, tenantCode);
  }

  return '';
}

function readEpochSecondsClaim(payload: Record<string, unknown>, key: string): number | null {
  const value = payload[key];
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value * 1000;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed * 1000;
    }
  }
  return null;
}

export function extractAuthenticatedAtFromToken(token: string | null): number | null {
  if (!token) {
    return null;
  }

  const payload = decodeJwtPayload(token);
  if (!payload) {
    return null;
  }

  return readEpochSecondsClaim(payload, 'auth_time') ?? readEpochSecondsClaim(payload, 'iat');
}

export function extractTokenProfile(
  token: string | null,
  context?: AuthContext | null,
): TokenProfile {
  if (!token) {
    return { displayName: '', systemName: extractSystemName({}, context) };
  }

  const payload = decodeJwtPayload(token);
  if (!payload) {
    return { displayName: '', systemName: extractSystemName({}, context) };
  }

  return {
    displayName: extractDisplayName(payload),
    systemName: extractSystemName(payload, context),
  };
}
